// 997 (R5d, owner R-AV 5): the invoice's FROZEN 'payments applied' links each payment to its receipt (the receipt stamped at issue, else the payment's live one), and ONE button
// 'Receipts for these payments' creates the missing receipts on demand (POST /sales-invoices/:id/payment-receipts, one Idempotency-Key per click, a retry re-sends it) and then
// opens them all in ONE PDF (this order's line highlighted). Receipts are never automatic. Read-only otherwise.
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { IcoLn } from '../../../shell/ActionIcons';
import { formatDate, formatMoneyExact } from '../../../utils/format';
import { keyFor, clearKey, serverMessage } from '../../../lib/idempotencyKey.mjs';
import { useOpenPdf } from '../../documents/DocPdfActions';
import { openReceiptRecord } from '../../../shell/openSaleRecord';
import useDocFieldRules from '../../documents/useDocFieldRules';

// pure: the payment rows of an invoice's applications (other kinds print as money applied but have no receipt)
export const paymentRowsOf = (apps) => (apps || []).filter((a) => a && a.source_kind === 'payment');
export const missingReceipts = (rows) => rows.filter((r) => !r.receipt_id || r.receipt_status === 'cancelled').length;

export default function InvoicePaymentsReceipts({ inv }) {
  const { t, i18n } = useTranslation('invoicing'); const lang = String(i18n.language || '').startsWith('fr') ? 'fr' : 'en';
  const [rows, setRows] = useState(() => paymentRowsOf(inv && inv.applications));
  const [err, setErr] = useState(''); const keyRef = useRef(null);
  const prepare = async () => {
    setErr('');
    if (!missingReceipts(rows)) return true;
    const r = await api.createInvoiceReceipts(inv.id, keyFor(keyRef, inv.id));
    if (!r || !r.ok) { setErr(serverMessage(r && r.data, lang) || t('qvReceiptsFailed')); return false; }
    clearKey(keyRef);
    const by = new Map(((r.data && r.data.receipts) || []).map((x) => [x.settlement_leg_id, x]));
    setRows((rs) => rs.map((a) => (by.has(a.source_id) ? { ...a, receipt_id: by.get(a.source_id).receipt_id, receipt_no: by.get(a.source_id).receipt_no, receipt_status: 'issued' } : a)));
    return true;
  };
  const pdf = useOpenPdf({ fetchPdf: () => api.getInvoiceReceiptsPdf(inv.id, lang), title: t('qvReceiptsForPayments') });
  const rules = useDocFieldRules('payment_receipt', inv?.partner_id || null);   // 47224 K-S10: printing issues the missing receipts
  const missing = rules.missing({});
  if (!inv || inv.kind !== 'invoice' || !rows.length) return null;
  const off = inv.status !== 'issued' ? t('qvReceiptsNotIssued') : (missingReceipts(rows) && rules.why(missing)) || '';
  return (
    <div data-testid="inv-qv-payments">
      <div className="sec-lab">{t('qvPaymentsApplied')}</div>
      <div className="gtable-wrap"><table className="gtable zebra" data-testid="inv-qv-payments-table">
        <thead><tr><th>{t('qvColPayment')}</th><th>{t('qvColDate')}</th><th className="r">{t('qvColAmount')}</th><th>{t('qvColReceipt')}</th></tr></thead>
        <tbody>{rows.map((a) => (
          <tr key={a.source_id} data-testid="inv-qv-payment-row">
            <td>{a.label || '—'}</td><td>{a.source_date ? formatDate(a.source_date) : '—'}</td><td className="r num">{formatMoneyExact(a.amount)}</td>
            <td>{a.receipt_id && a.receipt_no ? <button type="button" className="np-link mono" data-testid="inv-qv-receipt-link" onClick={() => openReceiptRecord({ id: a.receipt_id })}>{a.receipt_no}{a.receipt_status === 'cancelled' ? ` (${t('qvReceiptCancelled')})` : ''}</button>
              : <span className="sub" data-testid="inv-qv-receipt-none">{t('qvNoReceiptYet')}</span>}</td>
          </tr>))}</tbody>
      </table></div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
        <button type="button" className="btn sm" disabled={pdf.busy || !!off} aria-disabled={!!off} title={off || undefined} onClick={() => pdf.open(prepare)} data-testid="inv-qv-receipts-btn"><IcoLn name="print" /> {t('qvReceiptsForPayments')}</button>
        <span className="sub">{missingReceipts(rows) ? t('qvReceiptsWillCreate', { n: missingReceipts(rows) }) : t('qvReceiptsAllExist')}</span>
      </div>
      {(err || pdf.err) && <div className="ferr" role="alert" data-testid="inv-qv-receipts-error">{err || pdf.err}</div>}
    </div>
  );
}
